/**
 * PayeeSelector — combobox picker for Agent or Agency (mirrors ManagerSelector UX).
 * Pass `kind="agent"` or `kind="agency"`. Emits (id, name).
 *   - Agent id = NPN
 *   - Agency id = agency_id
 * Empty string = "any/all".
 */
import { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { agentAPI, agencyAPI } from '@/lib/api';

export function PayeeSelector({
  kind,                // 'agent' | 'agency'
  value,               // current id
  valueName,           // optional display fallback while list still loading
  onChange,            // (id, name) => void ; called with (null, null) on clear
  disabled = false,
  placeholder,
  className = '',
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = kind === 'agent'
      ? agentAPI.list()
      : agencyAPI.list();
    load
      .then((r) => {
        if (cancelled) return;
        // Both endpoints return an array directly
        const raw = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.agencies || r.data?.agents || []);
        const shaped = raw.map((row) => {
          if (kind === 'agent') {
            return {
              id: row.npn,
              label: `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email || row.npn,
              secondary: `NPN ${row.npn}${row.email ? ' · ' + row.email : ''}`,
            };
          }
          return {
            id: row.agency_id,
            label: row.agency_name || row.name || row.agency_id,
            secondary: row.parent_agency_name ? `Under ${row.parent_agency_name}` : '',
          };
        }).filter((r) => r.id);
        shaped.sort((a, b) => a.label.localeCompare(b.label));
        setItems(shaped);
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind]);

  const selected = useMemo(
    () => items.find((i) => i.id === value) || (value ? { id: value, label: valueName || value } : null),
    [items, value, valueName],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return items.slice(0, 200);
    const q = search.toLowerCase();
    return items.filter(
      (i) => i.label.toLowerCase().includes(q) || (i.secondary || '').toLowerCase().includes(q) || (i.id || '').toLowerCase().includes(q),
    ).slice(0, 200);
  }, [items, search]);

  const defaultPlaceholder = kind === 'agent' ? 'Select an agent…' : 'Select an agency…';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
            data-testid={testId}
          >
            <span className={cn('truncate', !selected && 'text-slate-400')}>
              {selected ? selected.label : (placeholder || defaultPlaceholder)}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={kind === 'agent' ? 'Search agents by name, email, NPN…' : 'Search agencies…'}
              value={search}
              onValueChange={setSearch}
              data-testid={testId ? `${testId}-search` : undefined}
            />
            <CommandList>
              {loading ? (
                <div className="py-6 text-center text-sm text-slate-500">Loading…</div>
              ) : (
                <>
                  <CommandEmpty>No {kind === 'agent' ? 'agents' : 'agencies'} found.</CommandEmpty>
                  <CommandGroup>
                    {filtered.map((i) => (
                      <CommandItem
                        key={i.id}
                        value={i.id}
                        onSelect={() => {
                          onChange(i.id, i.label);
                          setOpen(false);
                          setSearch('');
                        }}
                        data-testid={testId ? `${testId}-opt-${i.id}` : undefined}
                      >
                        <Check className={cn('mr-2 h-4 w-4', value === i.id ? 'opacity-100' : 'opacity-0')} />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{i.label}</span>
                          {i.secondary && <span className="text-xs text-slate-500 truncate">{i.secondary}</span>}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(null, null)}
          className="px-2"
          title={`Clear ${kind}`}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default PayeeSelector;
